import React, { useMemo, useState, useEffect, useRef, forwardRef, useImperativeHandle, memo } from 'react';
import ReactECharts from 'echarts-for-react';
import { 
  parseISO, 
  getISOWeek, 
  getYear, 
  format, 
  startOfMonth, 
  endOfMonth,
  startOfWeek,
  endOfWeek,
  startOfDay,
  endOfDay,
  isWithinInterval
} from 'date-fns';
import TimeRangeSlider from '../TimeRangeSlider/TimeRangeSlider';
import { formatButtonValue } from '../KPIComponent/formatUtils';

// Update utility functions for UTC handling
const toUTC = (date) => {
  return new Date(Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds()
  ));
};

const getCompleteTimeUnitRange = (startDate, endDate, timeUnit) => {
  let start = toUTC(new Date(startDate));
  let end = toUTC(new Date(endDate));
  
  switch (timeUnit) {
    case 'month':
      start = startOfMonth(start);
      end = endOfMonth(end);
      break;
    case 'week':
      start = startOfWeek(start);
      end = endOfWeek(end);
      break;
    case 'day':
      start = startOfDay(start);
      end = endOfDay(end);
      break;
  }
  return [start.getTime(), end.getTime()];
};

const snapToTimeUnit = (timestamp, timeUnit) => {
  const date = toUTC(new Date(timestamp));
  switch (timeUnit) {
    case 'month':
      return startOfMonth(date).getTime();
    case 'week':
      return startOfWeek(date).getTime();
    case 'day':
      return startOfDay(date).getTime();
    default:
      return timestamp;
  }
};

const TimeChart = memo(forwardRef(({ 
  data, 
  timeRange, 
  onTimeRangeChange, 
  selectedTypes = [], // Add default empty array
  onEventTypeToggle
}, ref) => {
  const [timeUnit, setTimeUnit] = useState('day'); // 'day', 'week', or 'month'
  const [fullDateRange, setFullDateRange] = useState(null);
  const chartInstance = useRef(null);
  const fatalitiesChartInstance = useRef(null); // Add a separate ref for the fatalities chart
  const isHighlighting = useRef(false); // Prevent infinite loop
  const isTooltipSyncing = useRef(false);
  const [visibleData, setVisibleData] = useState([]);
  const containerRef = useRef(null);

  // useResizeObserver(containerRef, (entry) => {
  //   if (chartInstance.current) {
  //     chartInstance.current.resize();
  //   }
  // });

  // Expose setTimeRange method through ref
  useImperativeHandle(ref, () => ({
    setTimeRange: (newRange) => {
      let [start, end] = newRange;
      const startDate = toUTC(new Date(start));
      const endDate = toUTC(new Date(end));

      // Snap to time unit boundaries
      switch (timeUnit) {
        case 'month':
          start = startOfMonth(startDate).getTime();
          end = endOfMonth(endDate).getTime();
          break;
        case 'week':
          start = startOfWeek(startDate).getTime();
          end = endOfWeek(endDate).getTime();
          break;
        case 'day':
          start = startOfDay(startDate).getTime();
          end = endOfDay(endDate).getTime();
          break;
      }

      onTimeRangeChange([start, end]);
    }
  }));

  // Add a useEffect to snap dates on initial load
  useEffect(() => {
    if (timeRange && timeRange.length === 2) {
      const [start, end] = timeRange.map(t => toUTC(new Date(t)));
      const snappedStart = snapToTimeUnit(start, timeUnit);
      let snappedEnd;
      
      switch (timeUnit) {
        case 'month':
          snappedEnd = endOfMonth(end).getTime();
          break;
        case 'week':
          snappedEnd = endOfWeek(end).getTime();
          break;
        case 'day':
          snappedEnd = endOfDay(end).getTime();
          break;
        default:
          snappedEnd = end.getTime();
      }

      // Only update if the dates actually changed
      if (snappedStart !== timeRange[0] || snappedEnd !== timeRange[1]) {
        // onTimeRangeChange([snappedStart, snappedEnd]);
      }
    }
  }, []); // Empty dependency array ensures this only runs once on mount

  // Initialize full date range once when component mounts
  useEffect(() => {
    if (!fullDateRange && data.length > 0) {
      const dates = data.map(event => toUTC(parseISO(event.event_date)).getTime());
      const minDate = new Date(Math.min(...dates));
      const maxDate = new Date(Math.max(...dates));
      let [start, end] = getCompleteTimeUnitRange(minDate, maxDate, timeUnit);
      if (start < minDate) start = minDate;
      setFullDateRange([start, end]);
    }
  }, [data, fullDateRange, timeUnit]);

  // Modify time unit change handler to avoid triggering re-renders
  const handleTimeUnitChange = (unit) => {
    if (unit === timeUnit) return;
    
    setTimeUnit(unit);
    // Update time range without triggering chart re-render
    const start = toUTC(new Date(timeRange[0]));
    const end = toUTC(new Date(timeRange[1]));
    
    const snappedStart = snapToTimeUnit(start, unit);
    const snappedEnd = unit === 'month' 
      ? endOfMonth(end).getTime()
      : unit === 'week'
      ? endOfWeek(end).getTime()
      : endOfDay(end).getTime();

    if (snappedStart !== timeRange[0] || snappedEnd !== timeRange[1]) {
      onTimeRangeChange([snappedStart, snappedEnd]);
    }
  };

  // Define consistent event types and colors
  const eventTypeOrder = [
    'Battles',
    'Explosions/Remote violence',
    'Violence against civilians',
    'Riots',
    'Protests',
    'Strategic developments'
  ];
  
  // Get computed CSS variable values
  const getColor = (colorVar) => {
    return getComputedStyle(document.documentElement).getPropertyValue(colorVar).trim();
  };

  const eventColors = {
    'Battles': getColor('--color-battles'),
    'Explosions/Remote violence': getColor('--color-explosions'),
    'Violence against civilians': getColor('--color-violence'),
    'Riots': getColor('--color-riots'),
    'Protests': getColor('--color-protests'),
    'Strategic developments': getColor('--color-strategic')
  };

  // Update colors when theme changes
  useEffect(() => {
    const updatedColors = {
      'Battles': getColor('--color-battles'),
      'Explosions/Remote violence': getColor('--color-explosions'),
      'Violence against civilians': getColor('--color-violence'),
      'Riots': getColor('--color-riots'),
      'Protests': getColor('--color-protests'),
      'Strategic developments': getColor('--color-strategic')
    };
    setChartColors(updatedColors);
  }, []);

  // Store colors in state to trigger re-render when they change
  const [chartColors, setChartColors] = useState(eventColors);

  // Calculate total counts for the current time range
  const totalCounts = useMemo(() => {
    return data.reduce((acc, event) => {
      const eventDate = toUTC(parseISO(event.event_date)).getTime();
      if (eventDate >= timeRange[0] && eventDate <= timeRange[1]) {
        acc[event.event_type] = (acc[event.event_type] || 0) + 1;
      }
      return acc;
    }, {});
  }, [data, timeRange]);

  const totalFatalities = useMemo(() => {
    return data.reduce((acc, event) => {
      const eventDate = toUTC(parseISO(event.event_date)).getTime();
      if (eventDate >= timeRange[0] && eventDate <= timeRange[1]) {
        acc += event.fatalities || 0;
      }
      return acc;
    }, 0);
  }, [data, timeRange]);

  const chartData = useMemo(() => {
    const timeMap = {};
    const dates = new Set();

    // Adjust the date range to include complete time units
    const [rangeStart, rangeEnd] = getCompleteTimeUnitRange(
      toUTC(new Date(fullDateRange?.[0] || timeRange[0])),
      toUTC(new Date(fullDateRange?.[1] || timeRange[1])),
      timeUnit
    );
    
    const currentDate = toUTC(new Date(rangeStart));
    const endDate = toUTC(new Date(rangeEnd));

    // Create complete time periods array based on full date range
    const allPeriods = [];

    // Update time key and period start/end formatting
    while (currentDate <= endDate) {
      let timeKey;
      let periodStart, periodEnd;
      
      switch (timeUnit) {
        case 'day':
          timeKey = format(currentDate, 'dd MMM yy');
          periodStart = startOfDay(currentDate).getTime();
          periodEnd = endOfDay(currentDate).getTime();
          break;
        case 'week':
          { const weekNumber = getISOWeek(currentDate);
          timeKey = `W${String(weekNumber).padStart(2, '0')}`;
          periodStart = startOfWeek(currentDate).getTime();
          periodEnd = endOfWeek(currentDate).getTime();
          break; }
        case 'month':
          timeKey = format(currentDate, 'MMM yy');
          periodStart = startOfMonth(currentDate).getTime();
          periodEnd = endOfMonth(currentDate).getTime();
          break;
      }
      
      if (!timeMap[timeKey]) {
        timeMap[timeKey] = {
          timestamp: currentDate.getTime(),
          periodStart,
          periodEnd,
          key: timeKey,
          year: getYear(currentDate),
          counts: Object.fromEntries(eventTypeOrder.map(type => [type, 0])),
          fatalities: 0
        };
        allPeriods.push(timeKey);
      }
      
      // Increment date based on timeUnit
      switch (timeUnit) {
        case 'day':
          currentDate.setDate(currentDate.getDate() + 1);
          break;
        case 'week':
          currentDate.setDate(currentDate.getDate() + 7);
          break;
        case 'month':
          currentDate.setMonth(currentDate.getMonth() + 1);
          break;
      }
    }

    // Filter and process events
    const filteredData = selectedTypes.length > 0
      ? data.filter(event => selectedTypes.includes(event.event_type))
      : data;

    filteredData.forEach((event) => {
      const date = toUTC(parseISO(event.event_date));
      let timeKey;
      
      switch (timeUnit) {
        case 'day':
          timeKey = format(date, 'dd MMM yy');
          break;
        case 'week':
          const weekNumber = getISOWeek(date);
          timeKey = `W${String(weekNumber).padStart(2, '0')}`;
          break;
        case 'month':
          timeKey = format(date, 'MMM yy');
          break;
      }
      
      if (timeMap[timeKey]) {
        timeMap[timeKey].counts[event.event_type] = (timeMap[timeKey].counts[event.event_type] || 0) + 1;
        timeMap[timeKey].fatalities += event.fatalities || 0;
      }
    });

    // Create series with updated colors and opacity
    const series = eventTypeOrder.map(type => ({
      name: type,
      type: 'bar',
      stack: 'total',
      emphasis: { focus: 'series' },
      itemStyle: {
        color: chartColors[type]
      },
      data: allPeriods.map(period => {
        const periodData = timeMap[period];
        const isInRange = periodData && isWithinInterval(new Date(periodData.timestamp), {
          start: new Date(timeRange[0]),
          end: new Date(timeRange[1])
        });
        
        return {
          value: periodData?.counts[type] || 0,
          itemStyle: {
            color: chartColors[type],
            opacity: isInRange ? 1 : 0.15
          }
        };
      })
    }));

    // Prepare data for fatalities chart with consistent color
    const fatalitiesSeriesData = allPeriods.map(period => {
      const periodData = timeMap[period];
      const isInRange = periodData && isWithinInterval(new Date(periodData.timestamp), {
        start: new Date(timeRange[0]),
        end: new Date(timeRange[1])
      });

      return {
        value: periodData ? periodData.fatalities : 0,
        itemStyle: {
          color: getColor('--color-fatalities'),
          opacity: isInRange ? 1 : 0.15
        }
      };
    });

    return {
      timePeriods: allPeriods,
      series,
      timeMap,
      fatalitiesSeriesData,
      dateRange: [rangeStart, rangeEnd]
    };
  }, [data, timeRange, timeUnit, selectedTypes, fullDateRange, eventTypeOrder]);

  // Add handler for time range snapping
  const handleTimeRangeChange = (newRange) => {
    let [start, end] = newRange.map(timestamp => toUTC(new Date(timestamp)));

    switch (timeUnit) {
      case 'month':
        start = startOfMonth(start).getTime();
        end = endOfMonth(end).getTime();
        break;
      case 'week':
        start = startOfWeek(start).getTime();
        end = endOfWeek(end).getTime();
        break;
      case 'day':
        start = startOfDay(start).getTime();
        end = endOfDay(end).getTime();
        break;
    }

    onTimeRangeChange([start, end]);
  };

  // Synchronize bar widths by setting barWidth in both charts
  const barWidthValue = '60%'; // Adjust as needed for consistent width

  // Update option for the first chart
  const option = useMemo(() => ({
    color: Object.values(chartColors),
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      position: function (point, params, dom, rect, size) {
        // Tooltip positioning logic
        return [Math.max(point[0], 0), point[1]];
      }
    },
    textStyle: {
      fontFamily: 'var(--font-family-base)',
      fontSize: 'var(--font-size-md)',
      color: 'var(--color-text-primary)' // Using CSS variable for text color
    },
    grid: {
      left: '50px', // Fixed space for y-axis labels
      right: '20px', // Increase right margin slightly
      bottom: '60px',
      top: '4%',
      containLabel: false // Change to false to respect fixed left margin
    },
    xAxis: {
      type: 'category',
      data: chartData.timePeriods,
      axisLabel: {
        // rotate: 45,
        hideOverlap: true,
        align: 'center',
        fontFamily: 'var(--font-family-base)',
        fontSize: 'var(--font-size-md)'
      },
      axisTick: {
        alignWithLabel: true
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        fontFamily: 'var(--font-family-base)',
        fontSize: 'var(--font-size-md)',
        formatter: (value) => {
          // Format number with consistent spacing
          return value.toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          });
        }
      },
      name: 'Events',
      nameLocation: 'middle',
      nameGap: 38,       // Adjust gap as needed
      nameRotate: 90,
    },
    series: chartData.series.map((series, index) => ({
      ...series,
      barWidth: barWidthValue, // Set consistent bar width
      emphasis: {
        focus: 'none', // Change to 'none' to highlight entire stack
        scale: false
      },
      itemStyle: {
        cursor: 'pointer',
        color: series.itemStyle?.color || chartColors[series.name] || 'var(--color-text-primary)'
      }
    })),
    animation: true, // Re-enable animation
    animationDuration: 300, // Set a shorter duration for better performance
    animationDurationUpdate: 300,
    animationEasing: 'cubicInOut',
    progressive: 500, // Keep progressive rendering for large datasets
    progressiveThreshold: 3000
  }), [chartData, timeRange]);

  // Define option for the fatalities chart
  const optionFatalities = {
    color: [getColor('--color-fatalities')],
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      position: function (point, params, dom, rect, size) {
        return [Math.max(point[0], 0), point[1]];
      }
    },
    textStyle: {
      fontFamily: 'var(--font-family-base)',
      fontSize: 'var(--font-size-md)',
      color: 'var(--color-text-primary)' // Using CSS variable for text color
    },
    grid: {
      left: '50px',
      right: '20px',
      bottom: '40px',
      top: '4%',
      containLabel: false
    },
    xAxis: {
      type: 'category',
      data: chartData.timePeriods,
      axisLabel: {
        hideOverlap: true,
        align: 'center',
        fontFamily: 'var(--font-family-base)',
        fontSize: 'var(--font-size-md)'
      },
      axisTick: {
        alignWithLabel: true
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        fontFamily: 'var(--font-family-base)',
        fontSize: 'var(--font-size-md)',
        formatter: (value) => {
          return value.toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          });
        }
      },
      name: 'Fatalities',
      nameLocation: 'middle',
      nameGap: 38,       // Adjust gap as needed
      nameRotate: 90,
      splitNumber: 2,    // Reduce number of ticks
    },
    series: [{
      name: 'Fatalities',
      type: 'bar',
      barWidth: barWidthValue, // Set consistent bar width
      data: chartData.fatalitiesSeriesData,
      emphasis: { focus: 'series' },
      itemStyle: {
        color: getColor('--color-fatalities'),
        cursor: 'pointer'
      }
    }],
    animation: true, // Re-enable animation
    animationDuration: 300,
    animationDurationUpdate: 300,
    animationEasing: 'cubicInOut',
  };

  // Add chart click handler setup
  useEffect(() => {
    if (chartInstance.current) {
      const chart = chartInstance.current.getEchartsInstance ? 
        chartInstance.current.getEchartsInstance() : 
        chartInstance.current;
      
      // Remove any existing click handlers
      chart.off('click');
      
      // Add new click handler
      chart.on('click', (params) => {
        if (params.seriesType === 'bar') {
          const timeKey = chartData.timePeriods[params.dataIndex];
          const periodData = chartData.timeMap[timeKey];
          
          if (periodData) {
            onTimeRangeChange([periodData.periodStart, periodData.periodEnd]);
          }
        }
      });
    }
  }, [chartInstance.current, chartData.timePeriods, chartData.timeMap]);

  // Update useEffect to handle the fatalities chart
  useEffect(() => {
    if (fatalitiesChartInstance.current) {
      const chart = fatalitiesChartInstance.current.getEchartsInstance();
      chart.off('click');
      chart.on('click', (params) => {
        const timeKey = chartData.timePeriods[params.dataIndex];
        const periodData = chartData.timeMap[timeKey];
        if (periodData) {
          onTimeRangeChange([periodData.periodStart, periodData.periodEnd]);
        }
      });
    }
  }, [fatalitiesChartInstance.current, chartData.timePeriods, chartData.timeMap]);

  // Add effect to synchronize hover effects between charts
  useEffect(() => {
    if (!chartInstance.current || !fatalitiesChartInstance.current) return;
    
    const chart1 = chartInstance.current.getEchartsInstance ? 
      chartInstance.current.getEchartsInstance() : 
      chartInstance.current;
    const chart2 = fatalitiesChartInstance.current.getEchartsInstance ? 
      fatalitiesChartInstance.current.getEchartsInstance() : 
      fatalitiesChartInstance.current;

    if (!chart1 || !chart2) return;

    const handleHighlight = (sourceChart, targetChart, dataIndex) => {
      if (isHighlighting.current) return;
      isHighlighting.current = true;

      // Show tooltips on both charts
      sourceChart.dispatchAction({ type: 'showTip', dataIndex });
      targetChart.dispatchAction({ type: 'showTip', dataIndex });

      isHighlighting.current = false;
    };

    const handleUnhighlight = (sourceChart, targetChart) => {
      if (isHighlighting.current) return;
      isHighlighting.current = true;

      // Hide tooltips on both charts
      sourceChart.dispatchAction({ type: 'hideTip' });
      targetChart.dispatchAction({ type: 'hideTip' });

      isHighlighting.current = false;
    };

    // Remove existing handlers
    chart1.off('mouseover');
    chart1.off('mouseout');
    chart2.off('mouseover');
    chart2.off('mouseout');

    // Add new handlers
    chart1.on('mouseover', (params) => handleHighlight(chart1, chart2, params.dataIndex));
    chart1.on('mouseout', () => handleUnhighlight(chart1, chart2));
    chart2.on('mouseover', (params) => handleHighlight(chart2, chart1, params.dataIndex));
    chart2.on('mouseout', () => handleUnhighlight(chart2, chart1));

    return () => {
      chart1.off('mouseover');
      chart1.off('mouseout');
      chart2.off('mouseover');
      chart2.off('mouseout');
    };
  }, [chartInstance.current, fatalitiesChartInstance.current]);

  // Create a shared tooltip formatter
  const sharedTooltipFormatter = (params) => {
    let index = params[0]?.dataIndex ?? params.dataIndex;
    let tooltipItems = [];

    // Get data from the first chart
    chartData.series.forEach(series => {
      const dataItem = series.data[index];
      tooltipItems.push({
        name: series.name,
        value: dataItem.value,
        color: series.itemStyle?.color || chartColors[series.name] || 'var(--color-text-primary)'
      });
    });

    // Get data from the second chart (fatalities)
    const fatalitiesValue = chartData.fatalitiesSeriesData[index]?.value || 0;
    tooltipItems.push({
      name: 'Fatalities',
      value: fatalitiesValue,
      color: getColor('--color-fatalities')
    });

    // Build tooltip HTML
    let tooltipHtml = `<div>${chartData.timePeriods[index]}</div>`;
    tooltipItems.forEach(item => {
      tooltipHtml += `<div>
        <span style="display:inline-block;width:10px;height:10px;background-color:${item.color};margin-right:5px;"></span>
        ${item.name}: ${item.value}
      </div>`;
    });

    return tooltipHtml;
  };

  // Update tooltip options in both charts
  option.tooltip.formatter = sharedTooltipFormatter;
  optionFatalities.tooltip.formatter = sharedTooltipFormatter;

  return (
    <div ref={containerRef} style={{ width: '100%', position: 'relative', padding: '10px 15px', backgroundColor: 'var(--color-background-paper)' }}>
      <div className="chart-controls">
        <div className="button-container">
          {eventTypeOrder.map(type => (
            <button
              key={type}
              className={`btn-common ${selectedTypes.includes(type) ? 'active' : ''}`}
              onClick={() => onEventTypeToggle(type)}
              style={{
                padding: '4px 10px',
                fontSize: 'var(--font-size-md)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <span 
                className="event-color-dot" 
                style={{ 
                  backgroundColor: chartColors[type],
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  display: 'inline-block'
                }}
              />
              <span className="event-type-label">{type}</span>
              <span className="event-count">
                {formatButtonValue(totalCounts[type] || 0, (totalCounts[type] || 0) >= 1000 ? 'thousands' : 'number')}
              </span>
            </button>
          ))}
        </div>

        <div className="button-group">
          {[
            { value: 'day', label: 'D' },
            { value: 'week', label: 'W' },
            { value: 'month', label: 'M' }
          ].map(({ value, label }) => (
            <button
              key={value}
              className={`btn-common ${timeUnit === value ? 'active' : ''}`}
              onClick={() => handleTimeUnitChange(value)}
              style={{
                padding: '4px 10px',
                fontSize: 'var(--font-size-md)',
                minWidth: '40px'
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div style={{ height: 'auto', position: 'relative' }}>
        <ReactECharts
          key="timechart" // Add a fixed key to maintain chart instance
          ref={chartInstance}
          option={option}
          style={{ height: '270px', width: '100%' }}
          opts={{ 
            renderer: 'canvas',
            notMerge: true // Change to true to prevent merging
          }}
        />
        <div style={{ height: '130px', marginTop: '10px' }}>
          <ReactECharts
            ref={fatalitiesChartInstance}
            option={optionFatalities}
            style={{ height: '100%', width: '100%' }}
            opts={{ renderer: 'canvas', notMerge: true }}
          />
        </div>
        
        <div style={{ 
          position: 'absolute', 
          bottom: '127px', 
          left: '40px', // Match grid.left
          right: '30px', // Match grid.right
          paddingBottom: '7px'
        }}>
          <h4 style={{
            'fontSize': '13px',
            'position': 'relative',
            'top': '39px',
            'left': '-40px',
          }}>Fatalities</h4>

          <TimeRangeSlider 
            range={[chartData.dateRange[0], chartData.dateRange[1]]}
            value={timeRange}
            onChange={handleTimeRangeChange}
            timeUnit={timeUnit}
          />
        </div>
        
      </div>
    </div>
  );
}));

TimeChart.displayName = 'TimeChart';

export default TimeChart;